import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { apiFetch } from '../../services/api-client';
import LiteMemberPanel, { liteErrorCopy, type LiteMember } from './LiteMemberPanel';

// Staff-side digital rolodex — some shops keep customers' paper cards in a box
// behind the counter, flipping through it to find a name. This replaces that
// box: search-tap-stamp, no card, no customer phone. Mirrors ScanPassModal's
// full-screen modal chrome; reuses LiteMemberPanel for the row-tap detail view
// rather than duplicating its stamp/redeem/rename/trade-in/link logic.
//
// It lists BOTH customer tiers. It used to show `lite_members` alone, which
// meant app customers were unfindable by name and — worse — a lite member who
// upgraded to a real account vanished from the book, reading behind the counter
// as "a regular went missing" at the moment they did what we asked.
//
// The tiers are listed together but are not interchangeable. A lite balance is
// Postgres-authoritative and stampable in place; an app customer's is on-chain
// and only their own device can submit the write. So app rows carry no stamp
// count (any number would be a guess) and route to the pass scanner instead of
// offering a stamp button that cannot work.

export interface CounterBookMember {
  id: string;
  tier: 'lite' | 'app';
  display_name: string | null;
  stamps: number | null;
  last_visit_at: string | null;
  card_code: string | null;
  address: string | null;
  trade_in_at: string | null;
  migrated_at: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  shopId: string;
  /** Hand off to the pass scanner — the only way to stamp an app customer. */
  onScanPass?: () => void;
}

const DEBOUNCE_MS = 300;

/** Lite rows carry every field the panel needs; the nullable types exist only
 * because app rows share the shape. */
function toLiteMember(m: CounterBookMember): LiteMember {
  return {
    id: m.id,
    card_code: m.card_code ?? '',
    display_name: m.display_name,
    stamps: m.stamps ?? 0,
    trade_in_at: m.trade_in_at,
    migrated_at: m.migrated_at,
    last_visit_at: m.last_visit_at,
  };
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'JUST NOW';
  if (mins < 60) return `${mins}M AGO`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}H AGO`;
  const days = Math.floor(hours / 24);
  return `${days}D AGO`;
}

export default function CounterBook({ open, onClose, shopId, onScanPass }: Props) {
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState<CounterBookMember[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LiteMember | null>(null);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  async function loadMembers(q: string) {
    const id = ++requestIdRef.current;
    setLoading(true);
    setListError(null);
    try {
      const r = await apiFetch<{ members: CounterBookMember[]; truncated?: boolean }>('/api/stamp', {
        action: 'lite-list',
        shopId,
        query: q,
      });
      if (requestIdRef.current !== id) return; // stale response, a newer search superseded it
      setMembers(r.members);
      setTruncated(Boolean(r.truncated));
    } catch (e) {
      if (requestIdRef.current !== id) return;
      setListError(liteErrorCopy(e, 'COULD NOT LOAD MEMBERS'));
    } finally {
      if (requestIdRef.current === id) setLoading(false);
    }
  }

  // Fresh session on every open. The dashboard mounts this modal permanently
  // (open just hides it), so component state survives close/reopen — reset
  // search/selection/create-form each time, same lesson as ScanPassModal.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(null);
    setCreating(false);
    setNewName('');
    setCreateError(null);
    setListError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    loadMembers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shopId]);

  function handleQueryChange(next: string) {
    setQuery(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadMembers(next), DEBOUNCE_MS);
  }

  async function createMember() {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      await apiFetch('/api/stamp', {
        action: 'lite-create',
        shopId,
        displayName: name,
      });
      setCreating(false);
      setNewName('');
      await loadMembers(query);
    } catch (e) {
      setCreateError(liteErrorCopy(e, 'COULD NOT CREATE MEMBER'));
    } finally {
      setCreateBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background">
      {/* Full-screen surface — same chrome language as ShopModal, not a
          floating dark card. Coral header carries the pixel label + title;
          content sits on cream with white cards, matching ScanPassModal's
          restyled coral surface (which embeds this same LiteMemberPanel). */}
      <div className="w-full h-full max-w-md mx-auto flex flex-col overflow-hidden bg-background">
        <div className="flex-none relative overflow-hidden bg-coral rounded-b-[24px] px-[22px] pb-[18px] pt-[calc(env(safe-area-inset-top)+16px)]">
          <div className="absolute -top-[34px] -right-[26px] w-[126px] h-[126px] rounded-full bg-accent-alt" />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 p-2 text-white/90 hover:text-white"
            style={{ top: 'calc(env(safe-area-inset-top) + 8px)' }}
          >
            <X size={18} />
          </button>
          <p className="relative font-heading text-[8px] text-white/85 tracking-[1px] uppercase mb-2">
            COUNTER BOOK
          </p>
          <p className="relative font-grotesk font-bold text-[22px] leading-[1.1] text-white pr-8">
            {selected ? 'Member card' : 'Find a member'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col px-[22px] pt-5 pb-6">
          {selected ? (
            <LiteMemberPanel
              member={selected}
              onDone={() => {
                setSelected(null);
                // The panel may have stamped/redeemed/renamed — refetch so the
                // list doesn't show a stale count until the next open.
                loadMembers(query);
              }}
            />
          ) : (
            <>
              <input
                type="text"
                placeholder="SEARCH BY NAME…"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                className="bg-white rounded-full px-[18px] py-[13px] font-grotesk font-medium text-sm text-primary placeholder:text-body-grey border-2 border-hairline focus:border-coral outline-none"
              />

              <div className="flex-1 overflow-y-auto min-h-[120px] mt-3.5">
                {listError ? (
                  <p className="font-grotesk font-medium text-[13px] text-error py-6 text-center">{listError}</p>
                ) : loading && members.length === 0 ? (
                  <p className="font-heading text-[10px] text-body-grey py-6 text-center">LOADING…</p>
                ) : members.length === 0 ? (
                  <p className="font-heading text-[10px] text-body-grey py-6 text-center">
                    {query.trim() ? 'NO MATCHES' : 'NO MEMBERS YET — ADD ONE BELOW'}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {members.map((m) => (
                      <li key={m.id}>
                        <button
                          data-testid={`cb-row-${m.id}`}
                          onClick={() => (m.tier === 'app' ? onScanPass?.() : setSelected(toLiteMember(m)))}
                          className="w-full bg-white rounded-[10px] px-4 py-3 flex items-center justify-between gap-2 text-left"
                        >
                          <span className="font-grotesk font-bold text-[15px] text-primary truncate">
                            {m.display_name || 'UNNAMED CARD'}
                          </span>
                          <span className="flex items-center gap-3 shrink-0">
                            {m.tier === 'app' ? (
                              // No number: their balance is on-chain and not
                              // readable here, and a placeholder 0 would tell the
                              // barista a nine-stamp regular is empty.
                              <span className="font-heading text-[9px] text-coral">SCAN PASS</span>
                            ) : (
                              <span className="font-heading text-[9px] text-[#2f9440]">
                                {m.stamps} STAMPS
                              </span>
                            )}
                            <span className="font-grotesk font-medium text-[11px] text-body-grey">
                              {timeAgo(m.last_visit_at)}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {truncated && (
                // Say it rather than quietly stopping at the cap. A list that
                // silently ends reads as the whole book, which is how a capped
                // giveaway list hid customers here for weeks.
                <p className="font-grotesk font-medium text-[12px] text-coral mt-2 text-center">
                  Showing the most recent only — search to find anyone else
                </p>
              )}

              <div className="mt-3 pt-3 border-t border-hairline">
                {creating ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      aria-label="Member name"
                      placeholder="MEMBER NAME"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="flex-1 min-w-0 bg-white rounded-full px-[16px] py-[10px] font-grotesk font-medium text-sm text-primary placeholder:text-body-grey border-2 border-hairline focus:border-coral outline-none"
                    />
                    <button
                      onClick={createMember}
                      disabled={createBusy || !newName.trim()}
                      className="shrink-0 rounded-full bg-pixel-cyan text-teal-ink font-grotesk font-bold text-[13px] py-[10px] px-4 disabled:opacity-50"
                    >
                      {createBusy ? 'CREATING…' : 'CREATE'}
                    </button>
                    <button
                      onClick={() => { setCreating(false); setNewName(''); setCreateError(null); }}
                      className="shrink-0 font-grotesk font-bold text-[12px] text-body-grey hover:text-primary"
                    >
                      CANCEL
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setCreating(true)}
                    className="w-full rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-sm py-3"
                  >
                    + NEW MEMBER
                  </button>
                )}
                {createError && <p className="font-grotesk font-medium text-[13px] text-error mt-2">{createError}</p>}
              </div>

              <div className="mt-3 text-center">
                <Link
                  to="/shops/member-cards"
                  className="font-grotesk font-medium text-[12px] text-body-grey hover:text-coral"
                >
                  PRINT MEMBER CARDS →
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
